/**
 * WeaveIn.ai Platform - Emergency Communications API Service
 * Endpoints for emergency incident management, audience building,
 * wave escalation, safety check-in, and external alert feed.
 */

import axios from 'axios';
import { getApiBaseUrl } from '@/lib/utils';

const API_BASE = getApiBaseUrl();

// ============================================================================
// TYPES
// ============================================================================

export type IncidentType = 'natural_disaster' | 'public_health' | 'facility_emergency' | 'mass_casualty';
export type IncidentSeverity = 'advisory' | 'watch' | 'warning' | 'emergency';
export type IncidentStatus = 'active' | 'monitoring' | 'resolved' | 'closed';

export interface EmergencyIncident {
  id: number;
  organization_id: number | null;
  incident_name: string;
  incident_type: IncidentType;
  severity: IncidentSeverity;
  status: IncidentStatus;
  description: string | null;
  affected_geography: {
    zip_codes?: string[];
    counties?: string[];
    states?: string[];
    fema_regions?: string[];
  };
  affected_member_count: number;
  external_alert_id: string | null;
  external_alert_source: string | null;
  activated_by: number | null;
  activated_at: string | null;
  resolved_at: string | null;
  created_at: string | null;
}

export interface WaveStatus {
  wave_id: number;
  wave_number: number;
  channel: string;
  status: string;
  target_count: number;
  sent_count: number;
  delivered_count: number;
  responded_count: number;
  failed_count: number;
  delay_after_previous: string | null;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface SafetyStatusSummary {
  total_targeted: number;
  confirmed_safe: number;
  needs_help: number;
  shelter: number;
  evacuated: number;
  unknown: number;
  total_responded: number;
  unreachable: number;
  response_rate: number;
  by_channel: Record<string, number>;
  timeline: Array<{ hour: string; count: number; type: string }>;
}

export interface SafetyResponse {
  id: number;
  member_id: number;
  response_type: string;
  channel: string;
  raw_message: string | null;
  keyword: string | null;
  needs_followup: boolean;
  followup_task_id: number | null;
  responded_at: string | null;
  member_name: string;
  zip_code: string | null;
}

export interface NonResponder {
  member_id: number;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  zip_code: string | null;
  engagement_score: number;
  risk_level: string;
  churn_score: number;
  last_wave_sent: string | null;
  waves_attempted: number;
}

export interface AudiencePreview {
  total_members: number;
  by_risk_level: Record<string, number>;
  by_channel: Record<string, number>;
  avg_churn_score: number | null;
  avg_engagement_score: number | null;
  estimated_cost: Record<string, number>;
  zip_codes_covered: number;
  filters_applied: Record<string, any>;
}

export interface AudienceFilters {
  zip_codes?: string[];
  counties?: string[];
  states?: string[];
  risk_levels?: string[];
  min_engagement_score?: number;
  max_engagement_score?: number;
  consent_tier?: string;
}

export interface WaveConfig {
  wave: number;
  channel: string;
  delay_after_previous?: string;
  message_template: string;
  voice_preset?: string;
  target?: string;
}

export interface ExternalAlert {
  id: number;
  source: string;
  alert_id: string;
  alert_type: string;
  title: string;
  description: string | null;
  severity: string;
  affected_geography: Record<string, any>;
  matched_member_count: number;
  ingested_at: string | null;
}

export interface IncidentTimelineEvent {
  event_type: string;
  message: string;
  triggered_by: number | null;
  timestamp: string | null;
}

export interface IncidentDetail {
  incident: EmergencyIncident;
  waves: WaveStatus[];
  safety_status: SafetyStatusSummary;
  timeline: IncidentTimelineEvent[];
}

// ============================================================================
// API CLIENT
// ============================================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;
  return headers;
};

export const emergencyAPI = {
  // ---- Incidents ----

  listIncidents: async (status?: IncidentStatus): Promise<{ incidents: EmergencyIncident[] }> => {
    const response = await axios.get(`${API_BASE}/api/emergency/incidents`, {
      params: status ? { status } : {},
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  createIncident: async (data: {
    incident_name: string;
    incident_type: IncidentType;
    severity?: IncidentSeverity;
    description?: string;
    affected_geography?: Record<string, any>;
  }): Promise<{ id: number; created_at: string; status: string }> => {
    const response = await axios.post(`${API_BASE}/api/emergency/incidents`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getIncident: async (incidentId: number): Promise<IncidentDetail> => {
    const response = await axios.get(`${API_BASE}/api/emergency/incidents/${incidentId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateIncident: async (
    incidentId: number,
    data: { status?: IncidentStatus; severity?: IncidentSeverity; resolution_notes?: string },
  ): Promise<{ success: boolean }> => {
    const response = await axios.patch(`${API_BASE}/api/emergency/incidents/${incidentId}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // ---- Audience ----

  previewAudience: async (filters: AudienceFilters): Promise<AudiencePreview> => {
    const response = await axios.post(`${API_BASE}/api/emergency/audience/preview`, filters, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  saveAudienceSegment: async (
    incidentId: number,
    segmentName: string,
    filters: AudienceFilters,
  ): Promise<{ segment_id: number }> => {
    const response = await axios.post(
      `${API_BASE}/api/emergency/incidents/${incidentId}/audience/save`,
      filters,
      { params: { segment_name: segmentName }, headers: getAuthHeaders() },
    );
    return response.data;
  },

  // ---- Campaigns & Waves ----

  launchCampaign: async (data: {
    incident_id: number;
    audience_filters?: AudienceFilters;
    waves: WaveConfig[];
    bypass_quiet_hours?: boolean;
  }): Promise<{
    campaign_id: number;
    incident_id: number;
    wave_ids: number[];
    audience_count: number;
    status: string;
  }> => {
    const response = await axios.post(
      `${API_BASE}/api/emergency/incidents/${data.incident_id}/campaign/launch`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getWaveStatus: async (incidentId: number): Promise<{ waves: WaveStatus[] }> => {
    const response = await axios.get(`${API_BASE}/api/emergency/incidents/${incidentId}/waves`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  advanceWave: async (waveId: number): Promise<{ wave_id: number; non_responders: number; result: any }> => {
    const response = await axios.post(`${API_BASE}/api/emergency/waves/${waveId}/advance`, {}, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // ---- Safety Check-In ----

  getSafetyStatus: async (incidentId: number): Promise<SafetyStatusSummary> => {
    const response = await axios.get(`${API_BASE}/api/emergency/incidents/${incidentId}/safety-status`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getSafetyFeed: async (
    incidentId: number,
    limit: number = 50,
    offset: number = 0,
  ): Promise<{ responses: SafetyResponse[] }> => {
    const response = await axios.get(`${API_BASE}/api/emergency/incidents/${incidentId}/safety-feed`, {
      params: { limit, offset },
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  processSafetyResponse: async (data: {
    incident_id: number;
    member_id: number;
    raw_message: string;
    channel?: string;
  }): Promise<{ response_type: string; needs_followup: boolean; task_id: number | null }> => {
    const response = await axios.post(`${API_BASE}/api/emergency/safety/respond`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // ---- Non-Responders ----

  getNonResponders: async (
    incidentId: number,
    sortByRisk: boolean = true,
  ): Promise<{ non_responders: NonResponder[]; total: number }> => {
    const response = await axios.get(`${API_BASE}/api/emergency/incidents/${incidentId}/non-responders`, {
      params: { sort_by_risk: sortByRisk },
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // ---- External Alerts ----

  getExternalAlerts: async (filters?: {
    min_member_match?: number; source?: string; state?: string; severity?: string;
  }): Promise<{ alerts: ExternalAlert[] }> => {
    const response = await axios.get(`${API_BASE}/api/emergency/alerts/feed`, {
      params: filters || {},
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getAlertSources: async (): Promise<{ sources: Array<{
    id: string; name: string; description: string; api_url: string;
    alert_types: string[]; poll_frequency: string; status: string; data_includes: string;
    geographic_filter?: string; requires_api_key?: boolean;
  }> }> => {
    const response = await axios.get(`${API_BASE}/api/emergency/alerts/sources`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  pollAlerts: async (source?: string, state?: string): Promise<{
    poll_results: Record<string, any>;
    alerts: ExternalAlert[];
    total_new: number;
  }> => {
    const response = await axios.post(`${API_BASE}/api/emergency/alerts/poll`, {}, {
      params: { ...(source ? { source } : {}), ...(state ? { state } : {}) },
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  createIncidentFromAlert: async (
    alertId: number,
    incidentName?: string,
    severity?: string,
  ): Promise<{ incident_id: number; status: string }> => {
    const response = await axios.post(
      `${API_BASE}/api/emergency/alerts/${alertId}/create-incident`,
      {},
      {
        params: { incident_name: incidentName, severity },
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },
};
